// Alert scheduler service that periodically checks for new earthquakes and sends alerts
import calamityService from './calamityService';
import emailService from './emailService';

class AlertScheduler {
  constructor() {
    this.isRunning = false;
    this.intervalId = null;
    this.checkInterval = 5 * 60 * 1000; // 5 minutes in milliseconds
    this.lastChecked = null;
  }

  /**
   * Start the alert scheduler
   */
  start() {
    if (this.isRunning) {
      console.log('Alert scheduler is already running');
      return;
    }

    console.log('Starting alert scheduler...');
    this.isRunning = true;
    
    // Run immediately on start
    this.checkForNewEarthquakes();
    
    // Set up periodic checking
    this.intervalId = setInterval(() => {
      this.checkForNewEarthquakes();
    }, this.checkInterval);
  }

  /**
   * Stop the alert scheduler
   */
  stop() {
    if (!this.isRunning) {
      console.log('Alert scheduler is not running');
      return;
    }

    console.log('Stopping alert scheduler...');
    this.isRunning = false;
    
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Check for new earthquakes and send alerts
   */
  async checkForNewEarthquakes() {
    try {
      console.log('Checking for new earthquakes...');
      
      // Get earthquakes from the last check period
      const timeWindow = this.lastChecked 
        ? `${Math.ceil((Date.now() - this.lastChecked) / (1000 * 60))}m` 
        : '5m'; // Default to 5 minutes
      
      const data = await calamityService.getAllCalamities(timeWindow, 4.5);
      const earthquakes = data.events.filter(event => event.type === 'earthquake');
      
      if (earthquakes.length > 0) {
        console.log(`Found ${earthquakes.length} new earthquakes`);
        
        // Send alerts for each earthquake
        for (const earthquake of earthquakes) {
          await emailService.sendEarthquakeAlert(earthquake);
        }
      } else {
        console.log('No new earthquakes found');
      }
      
      this.lastChecked = Date.now();
    } catch (error) {
      console.error('Error checking for earthquakes:', error);
    }
  }

  /**
   * Get scheduler status
   * @returns {Object} Scheduler status information
   */
  getStatus() {
    return {
      isRunning: this.isRunning,
      lastChecked: this.lastChecked ? new Date(this.lastChecked).toISOString() : null,
      nextCheck: this.isRunning && this.lastChecked 
        ? new Date(this.lastChecked + this.checkInterval).toISOString() 
        : null,
      checkInterval: this.checkInterval,
      subscribers: emailService.getStats()
    };
  }
}

// Export a singleton instance
export default new AlertScheduler();